// src/routes/Waivers.tsx
//
// Phase 4 §4.10 — Place / cancel blind waiver bids.
//
// Bones are not deducted at placement; the waiver processor (Wednesday cron)
// awards winners and writes the ledger row. The UI surfaces the current
// balance so owners can size bids, plus the season's waiver-order priority
// list (used to break ties).
import { useMutation, useQuery } from 'convex/react';
import { type FormEvent, useState } from 'react';
import { api } from '../../convex/_generated/api';
import type { Doc, Id } from '../../convex/_generated/dataModel';

export default function Waivers() {
  const me = useQuery(api.franchises.myFranchise);
  const myBids = useQuery(api.waivers.getMyWaiverBids, {});
  const balance = useQuery(api.bones.getBalance, {});
  const order = useQuery(api.waivers.getWaiverOrder, {});
  const franchises = useQuery(api.franchises.listFranchises);
  const placeBid = useMutation(api.waivers.placeWaiverBid);
  const cancelBid = useMutation(api.waivers.cancelWaiverBid);

  const [playerId, setPlayerId] = useState<string>('');
  const [amount, setAmount] = useState<number>(1);
  const [years, setYears] = useState<number>(1);
  const [week, setWeek] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (me === undefined) return <div className="p-6">Loading…</div>;
  if (me === null) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">No franchise</h1>
        <p className="mt-2 text-sm text-gray-600">
          Claim a franchise first.{' '}
          <a className="underline" href="/franchises">
            Pick one →
          </a>
        </p>
      </div>
    );
  }

  const fById = new Map(
    ((franchises ?? []) as Doc<'franchises'>[]).map((f) => [f._id, f] as const),
  );

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!playerId.trim()) {
      setError('Player ID is required.');
      return;
    }
    setSubmitting(true);
    try {
      await placeBid({
        playerId: playerId.trim() as Id<'players'>,
        amount,
        contractYears: years,
        week,
      });
      setPlayerId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onCancel = async (bidId: Id<'waiverBids'>) => {
    setError(null);
    try {
      await cancelBid({ bidId });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold">Waivers</h1>
      <div className="mt-2 text-sm text-gray-600">
        Bones balance: <strong>{balance ?? '—'}</strong>
      </div>

      <section className="mt-4">
        <h2 className="font-semibold mb-2">Place a bid</h2>
        <form
          onSubmit={onSubmit}
          className="flex flex-wrap gap-3 items-end border rounded p-3 bg-white"
        >
          <label className="flex flex-col text-sm">
            <span className="text-gray-600 mb-1">Player ID</span>
            <input
              className="border rounded px-2 py-1 font-mono text-xs w-72"
              value={playerId}
              onChange={(e) => setPlayerId(e.target.value)}
              placeholder="players|abc123…"
            />
          </label>
          <label className="flex flex-col text-sm">
            <span className="text-gray-600 mb-1">Amount</span>
            <input
              type="number"
              min={0}
              className="border rounded px-2 py-1 w-24"
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
            />
          </label>
          <label className="flex flex-col text-sm">
            <span className="text-gray-600 mb-1">Years (1–5)</span>
            <input
              type="number"
              min={1}
              max={5}
              className="border rounded px-2 py-1 w-20"
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
            />
          </label>
          <label className="flex flex-col text-sm">
            <span className="text-gray-600 mb-1">Week</span>
            <input
              type="number"
              min={1}
              className="border rounded px-2 py-1 w-20"
              value={week}
              onChange={(e) => setWeek(Number(e.target.value))}
            />
          </label>
          <button
            type="submit"
            disabled={submitting}
            className="px-3 py-2 bg-blue-600 text-white rounded text-sm disabled:opacity-50"
          >
            {submitting ? 'Placing…' : 'Place Bid'}
          </button>
        </form>
      </section>

      {error && (
        <div className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <section className="mt-6">
        <h2 className="font-semibold mb-2">My pending bids</h2>
        {myBids === undefined ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : myBids.length === 0 ? (
          <p className="text-sm text-gray-500">No pending bids.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="py-1">Player</th>
                <th>Amount</th>
                <th>Years</th>
                <th>Week</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(myBids as Doc<'waiverBids'>[]).map((b) => (
                <tr key={b._id} className="border-b">
                  <td className="py-1 font-mono text-xs">{String(b.playerId)}</td>
                  <td>{b.amount}</td>
                  <td>{b.contractYears}</td>
                  <td>{b.week}</td>
                  <td className="text-right">
                    <button
                      type="button"
                      onClick={() => onCancel(b._id)}
                      className="text-sm text-red-600 hover:underline"
                    >
                      cancel
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="mt-6">
        <h2 className="font-semibold mb-2">Waiver order</h2>
        {order === undefined ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : order.length === 0 ? (
          <p className="text-sm text-gray-500">No waiver order seeded.</p>
        ) : (
          <ol className="text-sm list-decimal list-inside space-y-0.5">
            {(order as Doc<'waiverOrder'>[]).map((row) => {
              const f = fById.get(row.franchiseId);
              return (
                <li key={row._id}>
                  {f?.name ?? String(row.franchiseId)}
                  {f?._id === me._id && <span className="ml-2 text-xs text-blue-600">(you)</span>}
                </li>
              );
            })}
          </ol>
        )}
      </section>
    </div>
  );
}
